import React, { useState, useEffect } from "react";
import axios from "axios";
import { addDays, format, parseISO, startOfWeek, isValid } from "date-fns";

export default function HistorialGlobal({ registros, setRegistros, modoSupervisor = false }) {
  const [filtroFechaInicio, setFiltroFechaInicio] = useState("");
  const [filtroNombre, setFiltroNombre] = useState("");
  const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
  const [filtroFechaFin, setFiltroFechaFin] = useState("");
  const [todosLosNombres, setTodosLosNombres] = useState([]);
  const [rangoInicio, setRangoInicio] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));


  const camposPermitidos = modoSupervisor
    ? [
        "tension_arterial", "saturacion_de_oxigeno", "frecuencia_cardiaca", "frecuencia_respiratoria", "temperatura",
        "indice_glucemico", "llenado_capilar", "bano", "cambio_ropa", "curaciones", "medicamentos_extras",
        "desayuno", "colacion_manana", "comida", "colacion_tarde", "cena", "observaciones",
        "micciones_observaciones", "evacuaciones_observaciones"
      ]
    : [
        "desayuno", "colacion_manana", "comida", "colacion_tarde", "cena", "observaciones",
        "micciones_observaciones", "evacuaciones_observaciones"
      ];

  useEffect(() => {
    const obtenerCuidadores = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/usuarios`, {
          headers: { Authorization: token },
        });
        const usuarios = res.data.filter(u => ["standard", "supervisor", "admin"].includes(u.role));
        const nombresDesdeFormularios = [...new Set(registros.map(r => r.nombre).filter(Boolean))];
        const nombresUnicos = Array.from(new Set([...usuarios.map(u => u.username), ...nombresDesdeFormularios]));
        setTodosLosNombres(nombresUnicos);
      } catch (err) {
        console.error("Error obteniendo cuidadores:", err);
      }
    };
    obtenerCuidadores();
  }, [registros]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRegistroSeleccionado(prev => ({ ...prev, [name]: value }));
  };

  const toggleEvacuacion = (vez, tipo) => {
    setRegistroSeleccionado(prev => {
      const actuales = prev.evacuaciones?.[vez] || [];
      const updated = actuales.includes(tipo)
        ? actuales.filter(t => t !== tipo)
        : [...actuales, tipo];
      return {
        ...prev,
        evacuaciones: {
          ...prev.evacuaciones,
          [vez]: updated
        }
      };
    });
  };

  const handleGuardar = async () => {
    const token = localStorage.getItem("token");
    try {
      const payload = {};
      for (let campo of camposPermitidos) {
        payload[campo] = registroSeleccionado[campo] || "";
      }

      payload.micciones = registroSeleccionado.micciones || {};
      payload.evacuaciones = registroSeleccionado.evacuaciones || {};

      await axios.put(
        `${process.env.REACT_APP_API_URL}/formulario/${registroSeleccionado._id}`,
        payload,
        {
          headers: { Authorization: token },
        }
      );

      const actualizados = registros.map((r) =>
        r._id === registroSeleccionado._id ? { ...r, ...payload } : r
      );
      setRegistros(actualizados);
      setRegistroSeleccionado(null);
      alert("✅ Cambios guardados correctamente");
    } catch (error) {
      console.error("Error al guardar cambios:", error);
      alert("❌ Error al guardar");
    }
  };

  const registrosFiltrados = registros.filter(r => {
    const fechaRaw = r?.fecha?.substring(0, 10);
    const fechaDate = parseISO(fechaRaw);

    const dentroDeRango = isValid(fechaDate) && (
      (filtroFechaInicio || filtroFechaFin)
        ? (!filtroFechaInicio || fechaDate >= parseISO(filtroFechaInicio)) &&
          (!filtroFechaFin || fechaDate <= parseISO(filtroFechaFin))
        : fechaDate >= rangoInicio && fechaDate <= addDays(rangoInicio, 6)
    );

    const nombreIncluido = !filtroNombre || r.nombre === filtroNombre;
    return dentroDeRango && nombreIncluido;
  });

  return (
    <div className="card shadow p-4">
      <h4 className="text-primary mb-3">Historial Global</h4>

      <div className="d-flex gap-2 flex-wrap mb-3 align-items-end">
        <div>
          <label className="form-label">Fecha inicio:</label>
          <input
            type="date"
            className="form-control"
            value={filtroFechaInicio}
            onChange={(e) => setFiltroFechaInicio(e.target.value)}
          />
        </div>

        <div>
          <label className="form-label">Fecha fin:</label>
          <input
            type="date"
            className="form-control"
            value={filtroFechaFin}
            onChange={(e) => setFiltroFechaFin(e.target.value)}
          />
        </div>
        <div>
          <label className="form-label">Filtrar por cuidador:</label>
          <select
            className="form-select"
            value={filtroNombre}
            onChange={(e) => setFiltroNombre(e.target.value)}
          >
            <option value="">Todos los cuidadores</option>
            {todosLosNombres.map((n, i) => (
              <option key={i} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <button onClick={() => setRangoInicio(prev => addDays(prev, -7))}>⏪ Semana anterior</button>
          <button onClick={() => setRangoInicio(prev => addDays(prev, 7))}>Semana siguiente ⏩</button>
        </div>
        {!filtroFechaInicio && !filtroFechaFin && (
          <span className="text-muted">
            Semana: {format(rangoInicio, 'dd/MM/yyyy')} - {format(addDays(rangoInicio, 6), 'dd/MM/yyyy')}
          </span>
        )}
      </div>

      <table className="table table-striped">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Cuidador</th>
            <th>Residente</th>
            <th>Turno</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          {registrosFiltrados.map((r) => (
            <tr key={r._id}>
              <td>{new Date(r.fecha).toLocaleString("es-MX")}</td>
              <td>{r.nombre}</td>
              <td>{r.residente}</td>
              <td>{r.turno}</td>
              <td>
                <button
                  className="btn btn-info btn-sm me-2"
                  onClick={() =>
                    setRegistroSeleccionado({
                      ...r,
                      evacuaciones: {
                        "1": r.evacuaciones?.["1"] || [],
                        "2": r.evacuaciones?.["2"] || [],
                        "3": r.evacuaciones?.["3"] || [],
                        "4": r.evacuaciones?.["4"] || [],
                        "5": r.evacuaciones?.["5"] || [],
                        "6": r.evacuaciones?.["6"] || [],
                        "7": r.evacuaciones?.["7"] || [],
                        "no_evacuo": r.evacuaciones?.["no_evacuo"] || []
                      },
                      micciones: r.micciones || []
                    })
                  }
                >
                  Ver detalle
                </button>
                 <button
                  className="btn btn-danger btn-sm"
                  onClick={async () => {
                    const confirmar = window.confirm("¿Seguro que deseas eliminar este registro?");
                    if (!confirmar) return;

                    try {
                      const token = localStorage.getItem("token");
                      await axios.delete(`${process.env.REACT_APP_API_URL}/formulario/${r._id}`, {
                        headers: { Authorization: token },
                      });
                      const actualizados = registros.filter(reg => reg._id !== r._id);
                      setRegistros(actualizados);
                    } catch (err) {
                      console.error("Error al eliminar:", err);
                      alert("Error al eliminar el registro");
                    }
                  }}
                >
                  Eliminar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {registroSeleccionado && (
        <div className="card p-4 mt-4">
          <h5 className="text-primary">Editar registro</h5>
          <form>
            {camposPermitidos.map((campo) => (
              <div className="mb-3" key={campo}>
                <label className="form-label">{campo.replace(/_/g, " ").toUpperCase()}</label>
                {["micciones_observaciones", "evacuaciones_observaciones"].includes(campo) ? (
                  <textarea
                    name={campo}
                    className="form-control"
                    value={registroSeleccionado[campo] || ""}
                    onChange={handleChange}
                    rows={3}
                  />
                ) : (
                  <input
                    type="text"
                    name={campo}
                    className="form-control"
                    value={registroSeleccionado[campo] || ""}
                    onChange={handleChange}
                  />
                )}
              </div>
            ))}

            <div className="mb-3">
              <label className="form-label">Micciones:</label>
              <div className="table-responsive">
                <table className="table table-bordered text-center">
                  <thead>
                    <tr>
                      <th>#</th>
                      {["clara", "oscura", "con sedimento", "otro"].map(t => <th key={t}>{t.toUpperCase()}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {["1","2","3","4","5","6","7","no_micciono"].map(vez => (
                      <tr key={vez}>
                        <td>{vez === "no_micciono" ? "No miccionó" : `${vez}ª vez`}</td>
                        {["clara", "oscura", "con sedimento", "otro"].map(tipo => (
                          <td key={tipo}>
                            <input
                              type="checkbox"
                              checked={registroSeleccionado.micciones?.[vez]?.includes(tipo) || false}
                              onChange={() => {
                                setRegistroSeleccionado(prev => {
                                  const actuales = prev.micciones?.[vez] || [];
                                  const updated = actuales.includes(tipo)
                                    ? actuales.filter(t => t !== tipo)
                                    : [...actuales, tipo];
                                  return {
                                    ...prev,
                                    micciones: {
                                      ...prev.micciones,
                                      [vez]: updated
                                    }
                                  };
                                });
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Evacuaciones:</label>
              <div className="table-responsive">
                <table className="table table-bordered text-center">
                  <thead>
                    <tr>
                      <th>#</th>
                      {["tipo1","tipo2","tipo3","tipo4","tipo5","tipo6","tipo7"].map(tipo => (
                        <th key={tipo}>{tipo.toUpperCase()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {["1","2","3","4","5","6","7","no_evacuo"].map(vez => (
                      <tr key={vez}>
                        <td>{vez === "no_evacuo" ? "No evacuó" : `${vez}ª vez`}</td>
                        {["tipo1","tipo2","tipo3","tipo4","tipo5","tipo6","tipo7"].map(tipo => (
                          <td key={tipo}>
                            <input
                              type="checkbox"
                              checked={registroSeleccionado.evacuaciones?.[vez]?.includes(tipo) || false}
                              onChange={() => toggleEvacuacion(vez, tipo)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-center">
              <button type="button" className="btn btn-success" onClick={handleGuardar}>Guardar Cambios</button>
              <button type="button" className="btn btn-secondary ms-2" onClick={() => setRegistroSeleccionado(null)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
